import React, {
  FC,
  useRef,
  useEffect,
  useCallback,
  ChangeEvent,
  KeyboardEvent,
  ClipboardEvent,
} from "react";

import "./codeInput.scss";

interface CodeInputProps {
  onCodeComplete: (code: string) => void;
  onCodeChange?: (code: string) => void;
  hasError?: boolean;
  errorMessage?: string;
  codeFieldLength?: number;
}

const CodeInput: FC<CodeInputProps> = ({
  onCodeComplete,
  onCodeChange,
  hasError = false,
  errorMessage,
  codeFieldLength = 5,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Digits live in a ref, not React state, and the fields are uncontrolled.
  // Typing therefore never triggers a re-render or controlled-value
  // reconciliation — that reconciliation is what dropped digits on mobile when
  // the user typed faster than React could commit. See UPP-5727.
  const valuesRef = useRef<string[]>(Array(codeFieldLength).fill(""));

  // Stable references to the latest callbacks so the typing handlers don't need
  // to be re-created and can be read without re-subscribing effects.
  const onCodeCompleteRef = useRef(onCodeComplete);
  const onCodeChangeRef = useRef(onCodeChange);
  const lastCompletedCodeRef = useRef<string>("");

  useEffect(() => {
    onCodeCompleteRef.current = onCodeComplete;
  }, [onCodeComplete]);

  useEffect(() => {
    onCodeChangeRef.current = onCodeChange;
  }, [onCodeChange]);

  // Keep the digit buffer sized to the field count, preserving existing entries.
  useEffect(() => {
    const next = Array(codeFieldLength).fill("");
    const carry = Math.min(next.length, valuesRef.current.length);
    for (let i = 0; i < carry; i++) {
      next[i] = valuesRef.current[i];
    }
    valuesRef.current = next;
  }, [codeFieldLength]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const emitCode = useCallback(() => {
    const code = valuesRef.current.join("");
    onCodeChangeRef.current?.(code);

    // Only fire onCodeComplete when the code is full and different from the last
    // completed code, so retries/edits don't resubmit the same value.
    if (
      code.length === codeFieldLength &&
      code !== lastCompletedCodeRef.current
    ) {
      lastCompletedCodeRef.current = code;
      onCodeCompleteRef.current(code);
    }

    if (code.length < codeFieldLength) {
      lastCompletedCodeRef.current = "";
    }
  }, [codeFieldLength]);

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const digit = e.target.value.replace(/\D/g, "").slice(-1);

    // Reflect the sanitized value back into the uncontrolled input (e.g. when a
    // non-digit was typed, clear it).
    if (e.target.value !== digit) {
      e.target.value = digit;
    }

    valuesRef.current[index] = digit;

    if (digit && index < codeFieldLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    emitCode();
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !valuesRef.current[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, codeFieldLength);

    if (!pastedData) return;

    const next = Array(codeFieldLength).fill("");
    for (let i = 0; i < pastedData.length; i++) {
      next[i] = pastedData[i];
    }
    valuesRef.current = next;

    inputRefs.current.forEach((input, i) => {
      if (input) input.value = next[i] ?? "";
    });

    const nextIndex = Math.min(pastedData.length, codeFieldLength - 1);
    inputRefs.current[nextIndex]?.focus();

    emitCode();
  };

  return (
    <div className="code-input">
      <div className="code-input__fields">
        {Array.from({ length: codeFieldLength }).map((_, index) => (
          <input
            key={index}
            ref={(ref) => {
              inputRefs.current[index] = ref;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            defaultValue=""
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className={`code-input__field ${hasError ? "code-input__field--error" : ""}`}
          />
        ))}
      </div>
      {hasError && errorMessage && (
        <div className="code-input__error-message">{errorMessage}</div>
      )}
    </div>
  );
};

export default CodeInput;
